/**
 * AgentAssetsTab Component
 * AIDLC Administrative Backend
 *
 * Displays agent scan results and reports grouped by template step action.
 * Latest 3 results are always visible per category; "View all" opens a modal.
 */

import { useState, useEffect, useCallback } from 'react';
import { getAgentAssets } from '../../services/agentAssetService';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { Badge } from '../shared/Badge';
import type { AgentAsset, AssetResult, AssetSeverity } from '../../types/agentAsset';

interface Props {
  agentId: string;
  agentName: string;
}

const VISIBLE_COUNT = 3;

const ACTION_LABELS: Record<string, { label: string; icon: string }> = {
  'owasp-scan': { label: 'OWASP Scans', icon: '🛡️' },
  'dependency-audit': { label: 'Dependency Audits', icon: '📦' },
  'infra-review': { label: 'Infrastructure Reviews', icon: '🏗️' },
  'report': { label: 'Audit Reports', icon: '📋' },
};

const RESULT_BADGE: Record<AssetResult, { variant: 'success' | 'danger' | 'warning' | 'info'; label: string }> = {
  pass: { variant: 'success', label: 'PASS' },
  fail: { variant: 'danger', label: 'FAIL' },
  warning: { variant: 'warning', label: 'WARNING' },
  info: { variant: 'info', label: 'INFO' },
};

const SEVERITY_COLORS: Record<AssetSeverity, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800',
  info: 'bg-gray-100 text-gray-600',
};

export function AgentAssetsTab({ agentId, agentName }: Props) {
  const [assets, setAssets] = useState<AgentAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalAction, setModalAction] = useState<string | null>(null);
  const [expandedAsset, setExpandedAsset] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await getAgentAssets(agentId);
        if (!cancelled) setAssets(data);
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [agentId]);

  // Escape key closes modal
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setModalAction(null);
  }, []);

  useEffect(() => {
    if (!modalAction) return;
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [modalAction, handleEscape]);

  if (loading) return <LoadingSpinner message="Loading assets..." size="sm" />;

  // Group by templateStepAction
  const grouped = new Map<string, AgentAsset[]>();
  for (const asset of assets) {
    const key = asset.templateStepAction;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(asset);
  }

  if (grouped.size === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3">📂</div>
        <p className="text-sm text-gray-500">No assets or reports for {agentName} yet.</p>
        <p className="text-xs text-gray-400 mt-1">Scan results and reports will appear here as templates are executed.</p>
      </div>
    );
  }

  const modalAssets = modalAction ? (grouped.get(modalAction) || []) : [];
  const modalLabel = modalAction ? (ACTION_LABELS[modalAction]?.label || modalAction) : '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ldaja-black">Agent Assets</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            Scan results, audit reports, and other outputs from {agentName}
          </p>
        </div>
        <span className="text-xs text-gray-400">{assets.length} total assets</span>
      </div>

      {/* Action groups */}
      {Array.from(grouped.entries()).map(([action, items]) => {
        const config = ACTION_LABELS[action] || { label: action, icon: '📄' };
        const visible = items.slice(0, VISIBLE_COUNT);
        const hasMore = items.length > VISIBLE_COUNT;

        return (
          <div key={action} className="border border-gray-200 rounded-lg">
            {/* Group header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
              <div className="flex items-center gap-2">
                <span className="text-base">{config.icon}</span>
                <h4 className="text-sm font-semibold text-ldaja-black">{config.label}</h4>
                <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded-full font-medium">
                  {items.length}
                </span>
              </div>
              {hasMore && (
                <button
                  onClick={() => setModalAction(action)}
                  className="text-xs text-ldaja-blue hover:text-blue-700 font-medium transition-colors"
                >
                  View all ({items.length})
                </button>
              )}
            </div>

            {/* Asset cards */}
            <div className="divide-y divide-gray-100">
              {visible.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  expanded={expandedAsset === asset.id}
                  onToggle={() => setExpandedAsset(expandedAsset === asset.id ? null : asset.id)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* View All Modal */}
      {modalAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalAction(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-base">{ACTION_LABELS[modalAction]?.icon || '📄'}</span>
                <h3 className="text-lg font-bold text-ldaja-black">{modalLabel}</h3>
                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                  {modalAssets.length} results
                </span>
              </div>
              <button
                onClick={() => setModalAction(null)}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal content */}
            <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
              {modalAssets.map((asset) => (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  expanded={expandedAsset === asset.id}
                  onToggle={() => setExpandedAsset(expandedAsset === asset.id ? null : asset.id)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AssetCard({ asset, expanded, onToggle }: { asset: AgentAsset; expanded: boolean; onToggle: () => void }) {
  const badge = RESULT_BADGE[asset.result] || RESULT_BADGE.info;
  const findingsCount = asset.findings.length;
  const criticalCount = asset.findings.filter(f => f.severity === 'critical').length;
  const highCount = asset.findings.filter(f => f.severity === 'high').length;

  return (
    <div className="px-4 py-3 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-start justify-between cursor-pointer" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-ldaja-black truncate">{asset.title}</span>
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </div>
          <p className="text-xs text-gray-500 line-clamp-2">{asset.summary}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-[10px] text-gray-400">
              {new Date(asset.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
              {' '}
              {new Date(asset.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            {findingsCount > 0 && (
              <span className="text-[10px] text-gray-500">
                {findingsCount} finding{findingsCount !== 1 ? 's' : ''}
                {criticalCount > 0 && <span className="text-red-600 font-semibold ml-1">({criticalCount} critical)</span>}
                {criticalCount === 0 && highCount > 0 && <span className="text-orange-600 font-semibold ml-1">({highCount} high)</span>}
              </span>
            )}
            {findingsCount === 0 && asset.result === 'pass' && (
              <span className="text-[10px] text-green-600 font-medium">Clean scan</span>
            )}
          </div>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Expanded findings */}
      {expanded && asset.findings.length > 0 && (
        <div className="mt-3 space-y-2 pl-1">
          {asset.findings.map((finding, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase flex-shrink-0 ${SEVERITY_COLORS[finding.severity]}`}>
                {finding.severity}
              </span>
              <div>
                <span className="font-medium text-gray-800">{finding.title}</span>
                <p className="text-gray-500 mt-0.5">{finding.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {expanded && asset.findings.length === 0 && (
        <div className="mt-3 text-xs text-gray-400 italic pl-1">
          No findings — clean scan.
        </div>
      )}

      {/* Metadata */}
      {expanded && Object.keys(asset.metadata).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2 pl-1">
          {Object.entries(asset.metadata).map(([key, value]) => (
            <span key={key} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded">
              {key}: {String(value)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
